/**
 * Runtime command contract for staff manual VOB entry.
 * Browser-safe. Bots parse again before authorization or writes.
 */
import { z } from 'zod';

import { calendarDateSchema } from './coverage-command.schema';

export const billingServiceCodeValues = ['PSY', 'IOP', 'PCH'] as const;

export const vobBenefitGroupSchema = z
  .object({
    copay: z.number().min(0).optional(),
    coinsurance: z.number().min(0).max(100).optional(),
    deductibleTotal: z.number().min(0).optional(),
    deductibleRemaining: z.number().min(0).optional(),
    oopMaxTotal: z.number().min(0).optional(),
    oopMaxRemaining: z.number().min(0).optional(),
    visitLimitTotal: z.number().int().min(0).optional(),
    visitLimitRemaining: z.number().int().min(0).optional(),
  })
  .superRefine((val, ctx) => {
    const pairs: Array<[number | undefined, number | undefined, string, string]> = [
      [val.deductibleTotal, val.deductibleRemaining, 'deductibleRemaining', 'Deductible'],
      [val.oopMaxTotal, val.oopMaxRemaining, 'oopMaxRemaining', 'Out-of-pocket max'],
      [val.visitLimitTotal, val.visitLimitRemaining, 'visitLimitRemaining', 'Visit limit'],
    ];
    for (const [total, remaining, path, label] of pairs) {
      if (total !== undefined && remaining !== undefined && remaining > total) {
        ctx.addIssue({
          code: 'custom',
          message: `${label} remaining cannot exceed total`,
          path: [path],
        });
      }
    }
  });

export type VobBenefitGroup = z.infer<typeof vobBenefitGroupSchema>;

export function isBenefitGroupPopulated(group: VobBenefitGroup | undefined): boolean {
  if (!group) return false;
  return Object.values(group).some((value) => value !== undefined);
}

export const manualVobCommandSchema = z
  .object({
    coverageId: z.string().min(1, 'Coverage is required'),
    billingCode: z.enum(billingServiceCodeValues),
    inforce: z.boolean(),
    disposition: z.string().optional(),
    inNetwork: vobBenefitGroupSchema.optional(),
    outOfNetwork: vobBenefitGroupSchema.optional(),
    serviceDate: calendarDateSchema.optional(),
  })
  .superRefine((val, ctx) => {
    const inn = isBenefitGroupPopulated(val.inNetwork);
    const oon = isBenefitGroupPopulated(val.outOfNetwork);
    if (val.inforce && !inn && !oon) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one in-network or out-of-network benefit group is required when coverage is in force',
        path: ['inNetwork'],
      });
    }
  });

export type ManualVobCommand = z.infer<typeof manualVobCommandSchema>;

/** Form alias used by staff VOB surfaces. */
export const manualVobSchema = manualVobCommandSchema;
export type ManualVobFormInput = ManualVobCommand;

export const defaultManualVobFormValues: ManualVobFormInput = {
  coverageId: '',
  billingCode: 'PSY',
  inforce: true,
  inNetwork: {},
  outOfNetwork: {},
};
